import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro, fmtEuroCents } from "@/lib/format";
import { MONTHS } from "@/components/bizstats/KpiTab";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

const sel = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";
const num = (v) => new Intl.NumberFormat("el-GR", { maximumFractionDigits: 1 }).format(v || 0);

const Delta = ({ pct, id }) => {
  if (pct === null || pct === undefined) return <span className="text-[#6F6A60]" data-testid={id}>νέο</span>;
  const up = pct > 0;
  return (
    <span data-testid={id} className={`inline-flex items-center gap-0.5 font-semibold ${up ? "text-[#B4412A]" : pct < 0 ? "text-[#4A5A3E]" : "text-[#6F6A60]"}`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : pct < 0 ? <ArrowDownRight className="h-3 w-3" /> : null}{num(pct)}%
    </span>
  );
};

// Per item × month, current year vs previous year (qty or net value).
export const ItemsYoY = ({ year, options }) => {
  const [by, setBy] = useState("item");
  const [measure, setMeasure] = useState("net");
  const [cat1, setCat1] = useState("");
  const [supplier, setSupplier] = useState("");
  const [qIn, setQIn] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  useEffect(() => { const t = setTimeout(() => setQ(qIn), 350); return () => clearTimeout(t); }, [qIn]);
  const params = { year, by, measure, cat1, supplier, q };
  const { data, isLoading } = useQuery({ queryKey: ["erp-yoy", params], queryFn: async () => (await api.get("/erp/items/yoy", { params })).data });
  const fmt = (v) => (measure === "net" ? fmtEuro(v) : num(v));
  const prev = year - 1;
  return (
    <div className="space-y-4" data-testid="items-yoy">
      <div className="flex flex-wrap gap-3 items-end">
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Ανά</span>
          <select data-testid="yoy-by" className={sel} value={by} onChange={(e) => setBy(e.target.value)}>
            <option value="item">Είδος</option><option value="cat2">Κατηγορία 2η</option><option value="supplier">Προμηθευτής</option>
          </select></label>
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Μέτρο</span>
          <select data-testid="yoy-measure" className={sel} value={measure} onChange={(e) => setMeasure(e.target.value)}>
            <option value="net">Αξία (καθαρή)</option><option value="qty">Ποσότητα</option>
          </select></label>
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Κατηγορία 1η</span>
          <select data-testid="yoy-cat1" className={sel} value={cat1} onChange={(e) => setCat1(e.target.value)}>
            <option value="">Όλες</option>{(options?.cat1 || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select></label>
        <label className="space-y-1 w-full sm:w-64"><span className="block text-[10px] uppercase text-[#6F6A60]">Προμηθευτής</span>
          <select data-testid="yoy-supplier" className={`${sel} w-full`} value={supplier} onChange={(e) => setSupplier(e.target.value)}>
            <option value="">Όλοι</option>{(options?.supplier || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select></label>
        <label className="space-y-1"><span className="block text-[10px] uppercase text-[#6F6A60]">Αναζήτηση</span>
          <input data-testid="yoy-search" value={qIn} onChange={(e) => setQIn(e.target.value)} placeholder="π.χ. γάλα" className={`${sel} w-48`} /></label>
      </div>
      {isLoading || !data ? <p className="text-sm text-[#6F6A60]">Φόρτωση…</p> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[[`Σύνολο ${year}`, fmt(data.total_cur), "yoy-total-cur"], [`Σύνολο ${prev}`, fmt(data.total_prev), "yoy-total-prev"],
              ["Μεταβολή", <Delta pct={data.total_delta_pct} id="yoy-total-delta" />, null], ["Είδη", data.row_count, "yoy-count"]].map(([l, v, id]) => (
              <div key={l} className="rounded-2xl bg-white border border-[#E3DED4] p-4">
                <div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">{l}</div>
                <div className="text-lg font-semibold text-[#45423C] tabular" data-testid={id || undefined}>{v}</div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-auto max-h-[65vh]" data-testid="yoy-table">
            <table className="w-full text-xs">
              <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">{by === "item" ? "Είδος" : by === "cat2" ? "Κατηγορία" : "Προμηθευτής"}</th>
                  {by === "item" && <th className="px-2 py-2 text-left">Κατηγορία</th>}
                  <th className="px-2 py-2 text-right">{year}</th>
                  <th className="px-2 py-2 text-right">{prev}</th>
                  <th className="px-2 py-2 text-right">Διαφορά</th>
                  <th className="px-2 py-2 text-right">%</th>
                  <th className="px-2 py-2 text-right">Ποσ. {year}</th>
                  <th className="px-2 py-2 text-right">Ποσ. {prev}</th>
                  <th className="px-2 py-2 text-right">Τιμή {year}</th>
                  <th className="px-2 py-2 text-right">Τιμή {prev}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <React.Fragment key={r.key}>
                    <tr className="border-t border-[#E3DED4] hover:bg-[#FAF8F4] cursor-pointer" data-testid={`yoy-row-${i}`} onClick={() => setOpen(open === r.key ? null : r.key)}>
                      <td className="px-3 py-1.5 font-medium text-[#45423C] max-w-[280px] truncate" title={r.key}>{r.key}</td>
                      {by === "item" && <td className="px-2 text-[#6F6A60] max-w-[160px] truncate">{r.cat2 || r.cat1 || "—"}</td>}
                      <td className="px-2 text-right tabular font-semibold">{fmt(r.cur)}</td>
                      <td className="px-2 text-right tabular text-[#6F6A60]">{fmt(r.prev)}</td>
                      <td className={`px-2 text-right tabular ${r.delta > 0 ? "text-[#B4412A]" : r.delta < 0 ? "text-[#4A5A3E]" : ""}`}>{fmt(r.delta)}</td>
                      <td className="px-2 text-right"><Delta pct={r.delta_pct} /></td>
                      <td className="px-2 text-right tabular">{num(r.qty_cur)}</td>
                      <td className="px-2 text-right tabular text-[#6F6A60]">{num(r.qty_prev)}</td>
                      <td className="px-2 text-right tabular">{r.price_cur ? fmtEuroCents(r.price_cur) : "—"}</td>
                      <td className="px-2 text-right tabular text-[#6F6A60]">{r.price_prev ? fmtEuroCents(r.price_prev) : "—"}</td>
                    </tr>
                    {open === r.key && (
                      <tr className="bg-[#FAF8F4]" data-testid={`yoy-months-${i}`}>
                        <td colSpan={by === "item" ? 10 : 9} className="px-3 py-2">
                          <table className="text-[11px]">
                            <thead><tr className="text-[#6F6A60]"><th className="text-left pr-3"></th>{MONTHS.map((m) => <th key={m} className="px-2 text-right font-normal">{m.slice(0, 3)}</th>)}</tr></thead>
                            <tbody>
                              {[[year, r.months_cur], [prev, r.months_prev]].map(([y, ms]) => (
                                <tr key={y}><td className="pr-3 font-semibold text-[#45423C]">{y}</td>
                                  {MONTHS.map((_, k) => <td key={k} className="px-2 text-right tabular">{ms[String(k + 1)] !== undefined ? fmt(ms[String(k + 1)]) : "·"}</td>)}</tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
